import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

/**
 * 首頁專用動畫函數
 * Home page specific animation functions
 */

export const gamaGlobePulseDown = ( environment, onStart ) => {
    gsap.to( environment.scale, {
        x: 0.9,
        y: 0.9,
        z: 0.9,
        duration: 0.3,
        ease: "power3.inOut",
        onStart,
    } );
};

export const gamaGlobePulseUp = ( environment, onComplete ) => {
    gsap.to( environment.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.8,
        ease: "elastic.out(1.1, 0.3)",
        onComplete,
    } );
};

export const gamaGlobeScaleIn = ( environment ) => {
    gsap.fromTo(
        environment.scale,
        {
            x: 0,
            y: 0,
            z: 0,
        },
        {
            x: 1,
            y: 1,
            z: 1,
            duration: 0.7,
            ease: "power3.inOut"
        }
    );
};

export const gamaGlobeScaleOut = ( environment, onComplete ) => {
    gsap.fromTo( environment.scale, {
        x: 1,
        y: 1,
        z: 1,
    }, {
        x: 0,
        y: 0,
        z: 0,
        duration: 0.7,
        ease: "back.in(2)",
        onComplete
    } );
};

export const focusCutFadeIn = (
    environment,
    startSpinning,
    pageMountedCallback,
    focusCutObject,
) => {
    const {
        trigger,
        slider,
        slidesLength,
        swiper,
        callback,
        reverseCallback,
        onEnter,
        onEnterBack,
        onLeave,
        onLeaveBack,
        swiperProgress,
        scrollVelocity,
    } = focusCutObject;

    const width = window.innerWidth;
    const height = window.innerHeight;

    const slides = slider?.querySelectorAll( ".swiper-slide" ) || [];
    const otherSlides = Array.from( slides ).filter(
        ( slide ) => slide !== slides[ 0 ],
    );
    const offsetX = document.getElementById( "carousel-block" )?.getBoundingClientRect()?.left - document.getElementById( "focus-block" )?.getBoundingClientRect()?.left || 0;

    let hasStarted = false;
    let hasStarted2 = false;
    let progress;

    const timerProxy = { value: 0 };
    const mockSwiperProgress = { value: 0 };
    const heroTl = gsap.timeline( { delay: 1.1 } );
    const focusTl = gsap.timeline( { paused: true } );

    // 等待 1.15 秒後，執行 pageMountedCallback
    heroTl.to( timerProxy, {
        value: 1,
        duration: 0.15,
        onComplete: pageMountedCallback,
    } );

    focusTl
        // 球體向上移動
        .to( environment.position, {
            y: 0,
            duration: 1,
            ease: "elastic.out(0.8,0.6)",
            onComplete: startSpinning,
        } )
        // 輪播區塊向上移動，並記錄 progress
        .to(
            slider,
            {
                y:
                    width >= 1270
                        ? height * -0.65 + 0.5 * ( slider?.offsetHeight || 0 )
                        : width >= 640
                            ? height * -0.17
                            : width >= 393
                                ? -40
                                : width * -0.108,
                duration: 0.5,
                ease: "power3.inOut",
            },
            "-=1",
        )
        // 自動動畫紀錄點
        .addLabel( "heroFadeInEnd" )
        // 球體縮小
        .fromTo(
            environment.scale,
            {
                x: 1,
                y: 1,
                z: 1,
            },
            {
                x: 0,
                y: 0,
                z: 0,
                duration: 1,
                ease: width >= 1270 ? "power2.out" : "power3.out",
            },
        )
        // 球體向下移動
        .to(
            environment.position,
            {
                y: 1,
                duration: 1,
                ease: "power1.in",
            },
            "-=1",
        );

    // 輪播區塊隨之往下移動
    // 桌機版則加上輪播區塊初始變形、自動切換輪播
    if ( window.innerWidth < 1270 ) {

        gsap.set( slider, { x: 0 } );

        focusTl
            .to(
                slider,
                {
                    x: 0,
                    y: 0,
                    duration: 0.8,
                    ease: "linear",
                    onUpdate: function () {
                        if ( !hasStarted && this.progress() > 0 ) {
                            callback();
                            hasStarted = true;
                        }

                        if ( this.progress() === 1 ) {
                            reverseCallback();
                            hasStarted = false;
                        }
                    },
                },
                "-=1",
            ).to(
                timerProxy,
                {
                    value: 2,
                    duration: 1,
                    ease: "linear",
                },
            );
    } else {

        gsap.set( slider, { x: -0.19 * width } );

        focusTl
            .to(
                slider,
                {
                    x: offsetX,
                    y: 0,
                    duration: 0.75,
                    ease: "linear",
                    onUpdate: function () {
                        if ( !hasStarted && this.progress() > 0 ) {
                            callback();
                            hasStarted = true;
                        }

                        if ( this.progress() === 1 ) {
                            reverseCallback();
                            hasStarted = false;
                        }
                    },
                },
                "-=1",
            )
            .to(
                otherSlides,
                {
                    xPercent: -158.7,
                    translateZ: -1800,
                    duration: 0.75,
                    ease: "linear",
                },
                "-=1",
            )
            // 輪播區塊就定位
            .addLabel( "swiperInit" );

        for ( let i = 0; i < slidesLength - 1; i++ ) {
            if ( i === 0 ) {
                focusTl.to(
                    mockSwiperProgress,
                    {
                        value: 1,
                        duration: 0.5,
                        ease: "linear",
                    },
                    "swiperInit",
                );
            } else {
                focusTl.to(
                    mockSwiperProgress,
                    {
                        value: i + 1,
                        duration: 0.5,
                        ease: "linear",
                    },
                    `swiperInit+=${ i * 0.5 }`,
                );
            }
        }
    }

    const focusST = ScrollTrigger.create( {
        trigger,
        // 行動裝置需要有更上面一點的觸發點，避免下方網址列干擾
        start: width >= 1270 ? "top bottom" : "top bottom-=15%",
        end: width >= 1270 ? `+=${ ( slidesLength - 1 ) * 50 + 200 }%` : "+=180%",
        scrub: true,
        onEnter,
        onEnterBack,
        onLeave,
        onLeaveBack,
        onUpdate: ( self ) => {

            focusTl.progress( progress + self.progress * ( 1 - progress ) );

            if ( width >= 1270 ) {

                scrollVelocity.value = self.getVelocity();
                swiperProgress.value = Math.floor( mockSwiperProgress.value );

                if ( swiper ) {
                    if ( self.direction > 0 ) {
                        if ( self.progress > 0.2 && !hasStarted2 ) {
                            slider.classList.add( "focus-in-scroll" );
                            hasStarted2 = true;
                        }
                    } else {
                        if ( self.progress < 0.2 && hasStarted2 ) {
                            slider.classList.remove( "focus-in-scroll" );
                            hasStarted2 = false;
                        }
                    }
                }
            }


        },
    } );

    // 在 hero 自動動畫完成前不接管捲動 — 否則 user 在 hero 期間滑動會
    // 累積 ScrollTrigger 內部 progress，等 onComplete 賦值 progress 後
    // 第一個 onUpdate 會把 timeline 從 heroFadeInEnd 瞬間跳到累積位置
    // （視覺上：滑了一陣子才忽然縮小）。disable 期間頁面仍照常捲動。
    focusST.disable();

    focusTl.tweenTo( "heroFadeInEnd", {
        delay: 1.1,
        onComplete: () => {
            progress = focusTl.progress();
            // 若 user 已滑入 trigger 範圍，平滑追到對應 timeline 位置再
            // 把控制權交給 ScrollTrigger，避免 enable 瞬間的硬切。
            const scrollProgress = focusST.progress;
            if ( scrollProgress > 0 ) {
                const target = progress + scrollProgress * ( 1 - progress );
                const catchUp = { value: progress };
                gsap.to( catchUp, {
                    value: target,
                    duration: 0.2,
                    ease: "power2.out",
                    onUpdate: () => focusTl.progress( catchUp.value ),
                    onComplete: () => focusST.enable(),
                } );
            } else {
                focusST.enable();
            }
        },
    } );
    return { heroTl, focusTl };
};

export const aboutCutFadeIn = ( target, title, brand, gamaGlobe, trigger ) => {

    const childContent = trigger.querySelector( ".sticky" );
    const childContentHeight = childContent.offsetHeight;
    const ratio = ( window.innerHeight / childContentHeight ).toFixed( 2 );
    const locale = document.documentElement.lang;

    const splitTypes = title.querySelectorAll( ".slt-text-fill-animation" );

    let hasStarted = false;

    const tl = gsap
        .timeline( {
            scrollTrigger: {
                trigger,
                start: "top bottom",
                end: "+=350%",
                scrub: true,
                toggleActions: "play play reverse reverse",
                onUpdate: function ( self ) {
                    if (
                        self.progress > 0.2 &&
                        self.direction > 0 &&
                        !hasStarted
                    ) {
                        gsap.to( gamaGlobe, {
                            y: 0,
                            duration: 1,
                            delay: 0.2,
                            ease: "elastic.out(1,0.3)",
                        } );
                        hasStarted = true;
                    } else if (
                        self.progress < 0.2 &&
                        self.direction < 0 &&
                        hasStarted
                    ) {
                        gsap.to( gamaGlobe, {
                            y: -gamaGlobe.offsetHeight - gamaGlobe.offsetTop,
                            duration: 0.3,
                            ease: "power2.out",
                        } );
                        hasStarted = false;
                    }
                },
            },
        } )
        .to( target, {
            yPercent: -10,
            duration: ratio,
            ease: "linear",
        } );

    splitTypes.forEach( ( charElement, i ) => {
        const text = SplitText.create( charElement, { type: "chars", aria: "none", smartWrap: locale === "en" ? true : false } );

        if ( window.innerWidth < 640 ) {
            text.chars.forEach( ( char ) => {
                if ( char.parentElement !== charElement ) {
                    char.parentElement.style.display = "inline";
                }
            } );
        }

        tl.fromTo(
            text.chars,
            { color: "rgba(0,0,0,0.2)" },
            {
                color: "#000",
                duration: 4 / ( 3 * text.chars.length ),
                ease: "stepped",
                stagger: 2 / ( 3 * text.chars.length ),
            },
        );
    } );

    tl.to( brand, {
        x: window.innerWidth >= 1270 ? brand.getBoundingClientRect().right - brand.lastElementChild.getBoundingClientRect().right : 0,
        duration: window.innerWidth >= 640 ? 1.5 : 0,
        ease: "linear",
    } ).to(
        childContent,
        {
            y: Math.min( window.innerHeight - childContentHeight, 0 ),
            duration: window.innerWidth >= 640 ? 1 - ratio : 1 - ratio + 1.5,
            ease: "linear",
        },
        window.innerWidth >= 1270 ? "-=0" : "-=1",
    );

    return tl;
};

export const newsCutFadeIn = ( target, title, trigger, onEnter, onLeaveBack ) => {
    let hasStarted = false;

    const contentHeight = trigger?.getBoundingClientRect()?.height || 0;
    const ratio = ( window.innerHeight / contentHeight ).toFixed( 2 );
    const ratio2 = (
        ( window.innerHeight / contentHeight ) * window.innerWidth >= 1270
            ? 0.7
            : 0.1
    ).toFixed( 2 );

    const childTitle = trigger?.querySelector( ".news-title" );
    const childPosts = trigger?.querySelector( ".news-posts" );
    const childButton = trigger?.querySelector( ".news-button" );

    const tl = gsap
        .timeline( {
            scrollTrigger: {
                trigger,
                start: "top bottom",
                end: `+=${ contentHeight }`,
                scrub: true,
                onEnter,
                onLeaveBack,
                onUpdate: function ( self ) {
                    if (
                        self.progress > ratio2 &&
                        self.direction > 0 &&
                        !hasStarted
                    ) {
                        title.animate( { duration: 0.5, stagger: 0.02 } );
                        hasStarted = true;
                    } else if (
                        self.progress < ratio2 &&
                        self.direction < 0 &&
                        hasStarted
                    ) {
                        title.revert();
                        hasStarted = false;
                    }
                },
            },
        } )
        .to( target, {
            yPercent: -10,
            duration: ratio,
            ease: "linear",
        } )
        .to( childTitle, {
            alpha: 0.5,
            duration: ( 1 - ratio ) * 0.5,
            ease: "linear",
        } )
        .to(
            childTitle,
            {
                yPercent: -200,
                duration: 1 - ratio,
                ease: "linear",
            },
            `-=${ ( 1 - ratio ) * 0.5 }`,
        );

    if ( childPosts && childButton ) {
        tl.to(
            [ childPosts, childButton ],
            {
                y: Math.min( window.innerHeight - contentHeight, 0 ),
                duration: 1 - ratio,
                ease: "linear",
            },
            `-=${ 1 - ratio }`,
        );
    }

    return tl;
};

export const gplayCutFadeIn = (
    target,
    title,
    swiper,
    gamaGlobe,
    trigger,
    onEnter,
    onLeaveBack,
    onLeave,
) => {
    const childContent = trigger?.querySelector( ".slt-container" );
    const childContentHeight = childContent?.getBoundingClientRect()?.height || 0;

    let ratio =
        window.innerWidth >= 640
            ? 1 / 3
            : ( window.innerHeight / childContentHeight ).toFixed( 2 );
    let ratio1 =
        window.innerWidth >= 640
            ? 0.08
            : ( ( window.innerHeight / childContentHeight ) * 0.3 ).toFixed( 2 );
    let ratio2 = 0.3;

    let swiperProgress = { value: 0 };
    let hasStarted1 = false;
    let hasStarted2 = false;

    const tl = gsap
        .timeline( {
            scrollTrigger: {
                trigger,
                start: "top bottom",
                end:
                    window.innerWidth >= 640
                        ? "+=300%"
                        : `+=${ childContentHeight }`,
                scrub: true,
                onUpdate: function ( self ) {
                    if ( self.direction > 0 ) {
                        if ( self.progress > ratio1 && !hasStarted1 ) {
                            if ( title ) title.animate( { duration: 0.5, stagger: 0.02 } );
                            hasStarted1 = true;
                        }
                        if ( self.progress > ratio2 && !hasStarted2 ) {
                            if ( window.innerWidth >= 1270 ) {
                                gsap.to( gamaGlobe, {
                                    y: 0,
                                    duration: 0.8,
                                    ease: "cubic-bezier(0.83, 0, 0.17, 1)",
                                } );
                            }
                            hasStarted2 = true;
                        }
                    } else {
                        if ( self.progress < ratio1 && hasStarted1 ) {
                            if ( title ) title.revert();
                            hasStarted1 = false;
                        }
                        if ( self.progress < ratio2 && hasStarted2 ) {
                            if ( window.innerWidth >= 1270 ) {
                                gsap.to( gamaGlobe, {
                                    y:
                                        0.0441 * window.innerHeight +
                                        gamaGlobe.offsetHeight,
                                    duration: 0.8,
                                    ease: "cubic-bezier(0.83, 0, 0.17, 1)",
                                } );
                            }
                            hasStarted2 = false;
                        }
                    }
                },
                onEnter,
                onLeaveBack,
                onLeave,
            },
        } )
        .to( target, {
            yPercent: -10,
            duration: ratio,
            ease: "linear",
        } )
        .addLabel( "swiperInit" )
        .to( childContent, {
            y: Math.min( window.innerHeight - childContentHeight, 0 ),
            duration: 1 - ratio,
            ease: "linear",
        } );

    if ( window.innerWidth >= 640 ) {
        tl.to(
            swiperProgress,
            {
                value: 1,
                duration: ( 1 - ratio ) * 0.5,
                ease: "linear",
                onUpdate: function () {
                    if ( swiper && swiper.value )
                        swiper.value.setProgress( this.progress() * 0.5 );
                },
            },
            "swiperInit",
        ).to(
            swiperProgress,
            {
                value: 2,
                duration: ( 1 - ratio ) * 0.5,
                ease: "linear",
                onUpdate: function () {
                    if ( swiper && swiper.value )
                        swiper.value.setProgress( this.progress() * 0.5 + 0.5 );
                },
            },
            `swiperInit+=${ ( 1 - ratio ) * 0.5 }`,
        );
    }

    return tl;
};

export const experienceCutFadeIn = (
    target,
    title,
    slides,
    gamaGlobe,
    trigger,
) => {
    const childContent = trigger.querySelector( ".slt-container" );
    const childContentHeight = childContent.getBoundingClientRect().height;

    let ratio =
        window.innerWidth >= 640
            ? 1 / 2
            : ( window.innerHeight / childContentHeight ).toFixed( 2 );
    let ratio1 =
        window.innerWidth >= 640
            ? 0.2
            : ( ( window.innerHeight / childContentHeight ) * 0.3 ).toFixed( 2 );
    let ratio2 = 0.5;

    let hasStarted1 = false;
    let hasStarted2 = false;

    const slideChildren = slides.map( ( slide ) => {
        return slide.querySelector( "div" );
    } );

    const tl = gsap
        .timeline( {
            scrollTrigger: {
                trigger,
                start: "top bottom",
                end:
                    window.innerWidth >= 640
                        ? "+=200%"
                        : `+=${ childContentHeight }`,
                scrub: true,
                onUpdate: function ( self ) {
                    if ( self.direction > 0 ) {
                        if ( self.progress > ratio1 && !hasStarted1 ) {
                            title.animate( { duration: 0.5, stagger: 0.02 } );
                            hasStarted1 = true;
                        }
                        if ( self.progress > ratio2 && !hasStarted2 ) {
                            if ( window.innerWidth >= 1270 ) {
                                gsap.to( gamaGlobe, {
                                    y: 0,
                                    duration: 0.8,
                                    ease: "cubic-bezier(0.83, 0, 0.17, 1)",
                                } );
                            }
                            hasStarted2 = true;
                        }
                    } else {
                        if ( self.progress < ratio1 && hasStarted1 ) {
                            title.revert();
                            hasStarted1 = false;
                        }
                        if ( self.progress < ratio2 && hasStarted2 ) {
                            if ( window.innerWidth >= 1270 ) {
                                gsap.to( gamaGlobe, {
                                    y:
                                        0.0441 * window.innerHeight +
                                        gamaGlobe.offsetHeight,
                                    duration: 0.8,
                                    ease: "cubic-bezier(0.83, 0, 0.17, 1)",
                                } );
                            }
                            hasStarted2 = false;
                        }
                    }
                },
            },
        } )
        .to( target, {
            yPercent: -10,
            duration: ratio,
            ease: "linear",
        } )
        .to(
            slideChildren,
            {
                y: 0,
                stagger: ( index ) => {
                    if ( window.innerWidth >= 1270 )
                        return ( index * ( ratio - ratio1 ) ) / 8;
                    else if ( index === 1 ) return 0;
                    else if ( index === 0 ) return ( ratio - ratio1 ) / 8;
                    else return ( ratio - ratio1 ) / 4;
                },
                duration: ratio - ratio1,
                ease: "power2.inOut",
            },
            `-=${ ratio - ratio1 }`,
        )
        .to( childContent, {
            y: Math.min( window.innerHeight - childContentHeight, 0 ),
            duration: 1 - ratio,
            ease: "linear",
        } );

    return tl;
};

export const esgCutFadeIn = (
    target,
    titles,
    subtitles,
    content,
    trigger,
    environment,
    onEnter,
    onLeaveBack,
) => {
    const childContent = trigger?.querySelector( ".slt-container" );
    const targetChildContent = target?.querySelector( ".slt-container" );
    const childContentHeight = childContent?.getBoundingClientRect()?.height || 0;
    const contentText = content.querySelectorAll( ".block" );

    let ratio =
        window.innerWidth >= 640
            ? 1 / 2
            : ( window.innerHeight / childContentHeight ).toFixed( 2 );
    let ratio1 =
        window.innerWidth >= 640
            ? 0.2
            : ( ( window.innerHeight / childContentHeight ) * 0.3 ).toFixed( 2 );
    let ratio2 =
        window.innerWidth >= 640
            ? 0.23
            : ( ( window.innerHeight / childContentHeight ) * 0.32 ).toFixed( 2 );
    let ratio3 =
        window.innerWidth >= 640
            ? window.innerWidth >= 1270
                ? 0.4
                : 0.35
            : ( ( window.innerHeight / childContentHeight ) * 0.55 ).toFixed( 2 );
    let ratio4 =
        window.innerWidth >= 640
            ? window.innerWidth >= 1270
                ? 0.23
                : 0.4
            : ( ( window.innerHeight / childContentHeight ) * 0.75 ).toFixed( 2 );

    let hasTitleAnimationStarted = false;
    let hasSubtitleAnimationStarted = false;
    let hasEarthAnimationStarted = false;
    let hasContentAnimationStarted = false;

    contentText.forEach( ( text ) => {
        gsap.set( text, { yPercent: 100 } );
    } );

    const tl = gsap.timeline( {
        scrollTrigger: {
            trigger: trigger,
            start: "top bottom",
            end:
                window.innerWidth >= 640 ? "+=200%" : `+=${ childContentHeight }`,
            scrub: true,
            onEnter,
            onLeaveBack,
            onUpdate: function ( self ) {
                if ( self.direction > 0 ) {
                    if ( self.progress > ratio1 && !hasTitleAnimationStarted ) {
                        titles.forEach( ( title ) => {
                            if ( title ) title.animate( { duration: 0.5, stagger: 0.02 } );
                        } );
                        hasTitleAnimationStarted = true;
                    }
                    if (
                        self.progress > ratio2 &&
                        !hasSubtitleAnimationStarted
                    ) {
                        subtitles.forEach( ( subtitle ) => {
                            if ( subtitle ) subtitle.animate( {
                                duration: 0.5,
                                stagger: 0.02,
                            } );
                        } );
                        hasSubtitleAnimationStarted = true;
                    }
                    if ( self.progress > ratio3 && !hasEarthAnimationStarted ) {
                        gsap.to(
                            environment.position,
                            {
                                y:
                                    window.innerWidth >= 640
                                        ? window.innerWidth >= 1270
                                            ? 0.5
                                            : 0.7
                                        : 0,
                                duration: 1,
                                ease: "elastic.out(0.5,0.6)",
                            },
                        );

                        hasEarthAnimationStarted = true;
                    }
                    if ( self.progress > ratio4 && !hasContentAnimationStarted ) {
                        contentText.forEach( ( text ) => {
                            gsap.fromTo(
                                text,
                                { yPercent: 100 },
                                {
                                    yPercent: 0,
                                    duration: 0.3,
                                    ease: "linear",
                                },
                            );
                        } );
                        hasContentAnimationStarted = true;
                    }
                } else {
                    if ( self.progress < ratio1 && hasTitleAnimationStarted ) {
                        titles.forEach( ( title ) => {
                            if ( title ) title.revert();
                        } );
                        hasTitleAnimationStarted = false;
                    }
                    if ( self.progress < ratio2 && hasSubtitleAnimationStarted ) {
                        subtitles.forEach( ( subtitle ) => {
                            if ( subtitle ) subtitle.revert();
                        } );
                        hasSubtitleAnimationStarted = false;
                    }
                    if ( self.progress < ratio3 && hasEarthAnimationStarted ) {
                        gsap.to( environment.position, {
                            y: -3,
                            duration: 0.5,
                            ease: "linear",
                        } );
                        hasEarthAnimationStarted = false;
                    }
                    if ( self.progress < ratio4 && hasContentAnimationStarted ) {
                        contentText.forEach( ( text ) => {
                            gsap.fromTo(
                                text,
                                { yPercent: 0 },
                                {
                                    yPercent: 100,
                                    duration: 0.3,
                                    ease: "linear",
                                },
                            );
                        } );
                        hasContentAnimationStarted = false;
                    }
                }
            },
        },
    } );

    if ( target?.id === "shorts" ) {
        tl.to( targetChildContent, {
            yPercent: window.innerWidth < 640 ? -50 : -100,
            duration: ratio,
            ease: "linear",
        } );
    } else {
        tl.to( target, {
            yPercent: -10,
            duration: ratio,
            ease: "linear",
        } );
    }

    tl.to( childContent, {
        y:
            window.innerWidth < 640
                ? 0
                : Math.min( window.innerHeight - childContentHeight, 0 ),
        duration: 1 - ratio,
        ease: "linear",
    } );

    return tl;
};

export const headerChangeColor = ( start, end, setDark, setLight ) => {
    return gsap.timeline( {
        scrollTrigger: {
            trigger: start,
            start: "top top",
            endTrigger: end,
            end: "bottom top",
            scrub: true,
            onEnter: setDark,
            onEnterBack: setLight,
        },
    } );
};
